/**
 * Chat Service
 * 
 * This service handles all API calls related to chat and conversation history.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Get authentication headers
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Get conversation history
 * @param {string|null} sessionId - Optional session ID to get messages for. If null, returns list of all sessions.
 * @param {number} limit - Maximum number of messages to return (only used when sessionId is provided)
 * @returns {Promise<Object>} Conversation history response
 */
export const getConversationHistory = async (sessionId = null, limit = 100) => {
  const params = new URLSearchParams();
  if (sessionId) {
    params.append('session_id', sessionId);
  }
  if (limit) {
    params.append('limit', limit.toString());
  }

  const queryString = params.toString();
  const url = `${API_BASE}/chat/history${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get conversation history');
  }

  return await response.json();
};

/**
 * Get all conversation sessions for the current user
 * @returns {Promise<Object>} List of conversation sessions
 */
export const getAllConversationSessions = async () => {
  return getConversationHistory(null);
};

/**
 * Get messages for a specific session
 * @param {string} sessionId - Session ID to get messages for
 * @param {number} limit - Maximum number of messages to return
 * @returns {Promise<Object>} Messages for the session
 */
export const getSessionMessages = async (sessionId, limit = 100) => {
  return getConversationHistory(sessionId, limit);
};

